import {
  editor,
  type IDisposable,
  MarkerSeverity,
  MarkerTag,
  type Uri,
} from 'monaco-editor'

import type { LanguageWorkerAccessor, WorkerMapping } from '../worker/api'
import type {
  FPlusClientDiagnostic,
  ValidationResult,
} from '../worker/contracts/ValidationResult'

type ITextModel = editor.ITextModel

export class FPlusValidationProvider<T> {
  listeners = new Map<string, IDisposable>()
  private languageID: string
  private worker: LanguageWorkerAccessor<T>
  private asWorkerInput: WorkerMapping<T>
  private errorHandler: (arg: ValidationResult, model: ITextModel) => void

  constructor(
    languageID: string,
    worker: LanguageWorkerAccessor<T>,
    asWorkerInput: WorkerMapping<T>,
    errorHandler: (arg: ValidationResult, model: ITextModel) => void,
  ) {
    this.languageID = languageID
    this.worker = worker
    this.asWorkerInput = asWorkerInput
    this.errorHandler = errorHandler

    const onModelAdd = (model: ITextModel) => {
      let handle: undefined | NodeJS.Timeout = undefined

      if (model.getLanguageId() === this.languageID) {
        if (!this.listeners.has(model.id)) {
          const listener = model.onDidChangeContent(() => {
            // here we are Debouncing the user changes, so everytime a new change is done, we wait 500ms before validating
            // otherwise if the user is still typing, we cancel the
            clearTimeout(handle)
            handle = setTimeout(() => this.validate(model, model.uri), 500)
          })
          void this.validate(model, model.uri)
          this.listeners.set(model.id, listener)
        }
      }
    }
    editor.onDidCreateModel((listener) => onModelAdd(listener))
    editor.getModels().forEach((listener) => onModelAdd(listener))
  }

  private async validate(model: ITextModel, resource: Uri) {
    // get the worker proxy
    if (!model.isDisposed()) {
      const worker = await this.worker(resource)

      if (!model.isDisposed()) {
        const input = await this.asWorkerInput({ model })

        if (!input?.data) {
          return
        }

        const errorMarkers = await worker?.validation(input.data)

        // add the error markers and underline them with severity of Error
        editor.setModelMarkers(
          model,
          this.languageID,
          errorMarkers?.diagnostics?.map(toDiagnostics) || [],
        )

        if (errorMarkers) {
          this.errorHandler(errorMarkers, model)
        }
      }
    }
  }
}

function toDiagnostics(error: FPlusClientDiagnostic | null) {
  return {
    code: error?.code ?? '',
    severity: transformSeverity(error?.severity ?? 1),
    tags: error?.tags.map(transformTag),
    message: error?.message ?? 'something unexpected happened',
    startLineNumber: error?.startLine ?? 0,
    startColumn: error?.startColumn ?? 0,
    endLineNumber: error?.endLine ?? 0,
    endColumn: error?.endColumn ?? 0,
  }
}

function transformSeverity(severity: number): MarkerSeverity {
  switch (severity) {
    case 1:
      return MarkerSeverity.Error
    case 2:
      return MarkerSeverity.Warning
    case 3:
      return MarkerSeverity.Hint
    case 4:
      return MarkerSeverity.Info
    default:
      throw new Error(`Unknown severity: ${severity}`)
  }
}

function transformTag(tag: number): MarkerTag {
  switch (tag) {
    case 1:
      return MarkerTag.Unnecessary
    case 2:
      return MarkerTag.Deprecated
    default:
      throw new Error(`Unknown tag: ${tag}`)
  }
}
